import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import { config } from 'config';
import { wait } from 'libs/common';
import { user } from 'services/user';
import { DesignSwitch } from '../page-main/blocks/design-switch';
import { validationSchema } from './forms/login-form.validation';
import {
  Content,
  Panel,
  Logo,
  LangPill,
  PillOption,
  Form,
  InputWrap,
  Eye,
  FieldError,
  Submit,
  ApiError,
  Divider,
  ViewRow,
} from './page-auth.styled.cp';

const LANGS = [
  { code: 'ru', native: 'Русский' },
  { code: 'en', native: 'English' },
];

const BarrierLogo = ({ open }) => (
  <Logo width="168" height="75" viewBox="0 0 290 130" aria-hidden>
    <rect x="4" y="2" width="54" height="108" rx="12" fill="#14211a" stroke="#2a4a37" strokeWidth="1.5" />
    <circle cx="30" cy="32" r="10" fill={open ? '#3dba6f' : '#e5534b'} style={{ transition: 'fill 0.4s' }} />
    {/* стрела шлагбаума: поворачивается вокруг левого края (оси) */}
    <g
      style={{
        transformOrigin: '58px 30px',
        transform: open ? 'rotate(-60deg)' : 'rotate(0deg)',
        transition: 'transform 0.9s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <rect x="58" y="19" width="218" height="22" rx="11" fill="#eef5f0" />
      {[1, 3, 5, 7].map((i) => (
        <rect key={i} x={58 + i * 23} y="19" width="23" height="22" fill="#3dba6f" />
      ))}
    </g>
    {[0, 1, 2, 3, 4].map((i) => (
      <rect key={i} x={4 + i * 60} y="118" width="44" height="7" rx="3.5" fill="#1f2b25" />
    ))}
  </Logo>
);

const EyeIcon = ({ off }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="M3 3l18 18" />}
  </svg>
);

export const PageAuth = () => {
  const { t, i18n } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [barrierOpen, setBarrierOpen] = useState(false);

  /**
   * user login
   */
  const actionLogin = async (data, helpers) => {
    // шлагбаум поднимается только после успешного входа, затем переход на главную
    const beforeRedirect = async () => {
      setBarrierOpen(true);
      await wait(1000);
    };
    await user.login(data, { beforeRedirect }).catch((errors) => {
      helpers.setErrors({ api: errors });
    });
  };

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
  };

  const lang = i18n.language && i18n.language.indexOf('en') === 0 ? 'en' : 'ru';

  return (
    <Content>
      <Panel>
        <BarrierLogo open={barrierOpen} />

        {config.use_lang && (
          <LangPill>
            {LANGS.map((l) => (
              <PillOption key={l.code} type="button" className={l.code === lang ? 'active' : ''} onClick={() => changeLang(l.code)}>
                {l.native}
              </PillOption>
            ))}
          </LangPill>
        )}

        <Formik initialValues={{ username: '', password: '' }} validationSchema={validationSchema(t)} onSubmit={actionLogin}>
          {(f) => {
            const err = (name) => (f.touched[name] && f.errors[name] ? f.errors[name] : null);
            return (
              <Form onSubmit={f.handleSubmit} noValidate>
                <InputWrap className={err('username') ? 'error' : ''}>
                  <input
                    name="username"
                    autoComplete="username"
                    autoFocus
                    placeholder={t('forms.Логин')}
                    value={f.values.username}
                    onChange={f.handleChange}
                    onBlur={f.handleBlur}
                  />
                  {err('username') && <FieldError>{err('username')}</FieldError>}
                </InputWrap>
                <InputWrap className={err('password') ? 'error' : ''}>
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('forms.Пароль')}
                    value={f.values.password}
                    onChange={f.handleChange}
                    onBlur={f.handleBlur}
                  />
                  <Eye type="button" tabIndex={-1} aria-label="Показать пароль" onClick={() => setShowPassword((v) => !v)}>
                    <EyeIcon off={showPassword} />
                  </Eye>
                  {err('password') && <FieldError>{err('password')}</FieldError>}
                </InputWrap>
                <Submit type="submit" disabled={f.isSubmitting}>
                  {t('forms.Войти')}
                </Submit>
                {f.errors.api && <ApiError>{f.errors.api}</ApiError>}
              </Form>
            );
          }}
        </Formik>

        <Divider />
        <ViewRow>
          <span>{t('dock.Вид')}</span>
          <DesignSwitch />
        </ViewRow>
      </Panel>
    </Content>
  );
};
